import { Button, CheckboxFormField, ClassName, Form, IFormRender, Theme } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import DashboardRibbon from 'components/foundation/dashboard/DashboardRibbon/DashboardRibbon';
import { useToastOpen } from 'contexts/ToastContext';
import { usePositionUpdate } from 'hooks';
import { t } from 'i18next';
import { isEqual } from 'lodash';
import { useRef } from 'react';

export interface ISafetyInspectionsFormData {
  performInspections: boolean;
  manageInspections: boolean;
}

export interface ISafetyInspectionsFormProps {
  className?: ClassName;
  positionId: string;
  onSubmit: () => void;
  performInspections: boolean;
  manageInspections: boolean;
}

const SafetyInspectionsForm = (props: ISafetyInspectionsFormProps) => {
  const { className, positionId, onSubmit, performInspections, manageInspections } = props;

  const [, update] = usePositionUpdate();
  const toast = useToastOpen();
  const initialFormData = useRef<ISafetyInspectionsFormData>({
    performInspections,
    manageInspections,
  });

  const handleFormSubmit = async (formData: ISafetyInspectionsFormData) => {
    if (!isEqual(initialFormData, formData)) {
      try {
        await update({
          id: positionId,
          performInspections: formData.performInspections,
          manageInspections: formData.performInspections ? formData.manageInspections : false,
        });
      } catch (error) {
        toast({ label: t('Something went wrong. Please try again later.') });
      }
    }
    onSubmit();
  };

  const performLabel = () => (
    <>
      <h4>{t('Perform Safety Inspections')}</h4>
      <h6>
        {t('Allow the user to create inspections for themselves and perform inspections assigned to them by others.')}
      </h6>
    </>
  );

  const manageLabel = () => (
    <>
      <h4>{t('Manage Safety Inspections')}</h4>
      <h6>{t('View, assign, and delete inspections for others. Manage inspection checklists.')}</h6>
    </>
  );

  const renderFormFields = (form: IFormRender<ISafetyInspectionsFormData>) => (
    <div className={'m-5 pt-8'}>
      <CheckboxFormField name={'performInspections'} form={form} label={performLabel()} />
      {form.state.data.performInspections && (
        <DashboardRibbon theme={Theme.LIGHT}>
          <CheckboxFormField name={'manageInspections'} form={form} label={manageLabel()} />
        </DashboardRibbon>
      )}
      <Button onClick={form.handleSubmit} type="primary" className={'mt-4'}>
        {t('Update')}
      </Button>
    </div>
  );

  const classes = classNames('safety-inspections-form', className);

  return (
    <Form render={renderFormFields} onSubmit={handleFormSubmit} data={initialFormData.current} className={classes} />
  );
};

export default SafetyInspectionsForm;
